import type { LocationQuery } from 'vue-router';
import { createRouter, createWebHistory } from 'vue-router';

import { useRouterStore } from '@apps/main/new-desktop/core/stores';
import {
  TripPlannerPage,
  mapPathToTripPlannerPage,
} from '@apps/main/new-desktop/core/models/navigation';

import {
  exhaustiveGuard,
  isDefined,
  performanceMark,
  PerformancePoint,
} from '@libs/utils';

import { TripPlannerRoute } from './tripPlannerRoute';

const router = createRouter({
  history: createWebHistory(),
  routes: [TripPlannerRoute.config],
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition;
    }

    const isSameParent = to.matched[1]?.name === from.matched[1]?.name;

    return isSameParent ? false : { top: 0 };
  },
});

router.beforeEach((to, from) => {
  const previousPage = mapPathToTripPlannerPage(from.path);
  const nextPage = mapPathToTripPlannerPage(to.path);

  if (previousPage !== nextPage) {
    const routerStore = useRouterStore();
    routerStore.previousTripPlannerPage = previousPage;

    if (isDefined(nextPage)) {
      trackPageNavigationInitiatedPerformance(nextPage);
    }
  }

  // NOTE: This part redirects
  // * form: /travel/plan?pax=rpp
  // * to:   /travel/plan/overview?pax=rpp
  // which is required for meta redirection
  if (nextPage === TripPlannerPage.BaseURL) {
    return hasIngredientQueryParams(to.query)
      ? {
          name: TripPlannerPage.TripOverview,
          query: to.query,
        }
      : {
          name: TripPlannerPage.BuildYourTrip,
          query: to.query,
        };
  }

  // NOTE: This part redirects
  // * form: /travel/plan/overview?pax=rpp
  // * to:   /travel/plan/build-your-trip?pax=rpp
  // since TripOverview without ingredients is no longer valid
  if (
    nextPage === TripPlannerPage.TripOverview &&
    !hasIngredientQueryParams(to.query)
  ) {
    return { name: TripPlannerPage.BuildYourTrip, query: to.query };
  }

  // NOTE: This part redirects
  // * form: /travel/plan/build-your-trip?pax=rpp&i1=00ot&i2=polygon-id:517571+fltr:07000000000000000000000000sug:2ht&i3=00ot
  // * to:   /travel/plan/overview?pax=rpp&i1=00ot&i2=polygon-id:517571+fltr:07000000000000000000000000sug:2ht&i3=00ot
  // since TripOverview without ingredients is no longer valid
  if (
    nextPage === TripPlannerPage.BuildYourTrip &&
    hasIngredientQueryParams(to.query)
  ) {
    return { name: TripPlannerPage.TripOverview, query: to.query };
  }
});

function hasIngredientQueryParams(query: LocationQuery): boolean {
  return Object.keys(query).some((queryKey) => queryKey.match(/^i\d*$/));
}

function trackPageNavigationInitiatedPerformance(
  destinationPage: TripPlannerPage
): void {
  switch (destinationPage) {
    case TripPlannerPage.BuildYourTrip:
      performanceMark(PerformancePoint.BuildYourTripPageNavigationInitiated);
      break;

    case TripPlannerPage.TripOverview:
      performanceMark(PerformancePoint.TripOverviewPageNavigationInitiated);
      break;

    case TripPlannerPage.HotelSelection:
      performanceMark(PerformancePoint.HotelSelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.HotelDetailsInHotelSelection:
      performanceMark(PerformancePoint.HotelDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.ActivitySelection:
      performanceMark(
        PerformancePoint.ActivitySelectionPageNavigationInitiated
      );
      break;

    case TripPlannerPage.ActivityDetailsInActivitySelection:
      performanceMark(PerformancePoint.ActivityDetailsPageNavigationInitiated);
      break;

    case TripPlannerPage.FlightList:
      performanceMark(PerformancePoint.FlightSelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.FerryList:
      performanceMark(PerformancePoint.FerrySelectionPageNavigationInitiated);
      break;

    case TripPlannerPage.BaseURL:
    case TripPlannerPage.TripOverviewPrint:
    case TripPlannerPage.HotelDetails:
    case TripPlannerPage.ActivityDetails:
    case TripPlannerPage.CarTrip:
      break;

    default:
      exhaustiveGuard(destinationPage, 'Unhandled trip planner page');
  }
}

export default router;
